<template>
  <div>
    <!-- Default To: contact email when custom To is hidden -->
    <div
      v-if="allowToField && !showTo && defaultToEmail"
      class="input-group-wrap"
    >
      <div class="input-group small">
        <label class="input-group-label">
          {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.TO') }}
        </label>
        <div class="input-group-field">
          <span class="default-to-chip" :title="defaultToEmail">
            {{ defaultToEmail }}
          </span>
        </div>
      </div>
    </div>

    <!-- Editable To field -->
    <div v-if="showTo" class="input-group-wrap">
      <div class="input-group small" :class="{ error: $v.toEmailsVal.$error }">
        <label class="input-group-label">
          {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.TO') }}
        </label>
        <div class="input-group-field">
          <email-chip-input
            ref="toChipInput"
            v-model="$v.toEmailsVal.$model"
            :placeholder="
              $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.EMAIL_CHIP_PLACEHOLDER')
            "
            @change="onToEmailsChange"
            @blur="syncToEmails"
          />
        </div>
        <woot-button
          v-if="allowToField && canCollapseTo"
          variant="clear"
          size="small"
          icon="dismiss"
          :title="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.REMOVE_TO')"
          @click="handleRemoveTo"
        />
      </div>
      <span v-if="$v.toEmailsVal.$error" class="message">
        {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.ERROR') }}
      </span>
    </div>

    <div class="input-group-wrap">
      <div class="input-group small" :class="{ error: $v.ccEmailsVal.$error }">
        <label class="input-group-label">
          {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.LABEL') }}
        </label>
        <div class="input-group-field">
          <email-chip-input
            ref="ccChipInput"
            v-model="$v.ccEmailsVal.$model"
            :placeholder="
              $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.EMAIL_CHIP_PLACEHOLDER')
            "
            @change="syncCcEmails"
            @blur="syncCcEmails"
          />
        </div>
        <woot-button
          v-if="allowToField && !showTo"
          variant="clear"
          size="small"
          @click="handleAddTo"
        >
          {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.ADD_TO') }}
        </woot-button>
        <woot-button
          v-if="!showBcc"
          variant="clear"
          size="small"
          @click="handleAddBcc"
        >
          {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.ADD_BCC') }}
        </woot-button>
      </div>
      <span v-if="$v.ccEmailsVal.$error" class="message">
        {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.ERROR') }}
      </span>
    </div>

    <div v-if="showBcc" class="input-group-wrap">
      <div class="input-group small" :class="{ error: $v.bccEmailsVal.$error }">
        <label class="input-group-label">
          {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.LABEL') }}
        </label>
        <div class="input-group-field">
          <email-chip-input
            ref="bccChipInput"
            v-model="$v.bccEmailsVal.$model"
            :placeholder="
              $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.EMAIL_CHIP_PLACEHOLDER')
            "
            @change="onBccEmailsChange"
            @blur="syncBccEmails"
          />
        </div>
        <woot-button
          v-if="canCollapseBcc"
          variant="clear"
          size="small"
          icon="dismiss"
          :title="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.REMOVE_BCC')"
          @click="handleRemoveBcc"
        />
      </div>
      <span v-if="$v.bccEmailsVal.$error" class="message">
        {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.ERROR') }}
      </span>
    </div>
  </div>
</template>

<script>
import {
  parseEmailList,
  validEmailsByComma,
} from './helpers/emailHeadHelper';
import EmailChipInput from './EmailChipInput.vue';

export default {
  components: {
    EmailChipInput,
  },
  props: {
    ccEmails: {
      type: String,
      default: '',
    },
    bccEmails: {
      type: String,
      default: '',
    },
    toEmails: {
      type: String,
      default: '',
    },
    defaultToEmail: {
      type: String,
      default: '',
    },
    allowToField: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showTo: false,
      showBcc: false,
      ccEmailsVal: '',
      bccEmailsVal: '',
      toEmailsVal: '',
    };
  },
  computed: {
    // Row X only collapses To when there are no extra recipients beyond the
    // default contact — so it never wipes emails the agent added.
    canCollapseTo() {
      const emails = parseEmailList(this.toEmailsVal).map(email =>
        email.toLowerCase()
      );
      if (!emails.length) return true;

      const defaultEmail = (this.defaultToEmail || '').toLowerCase();
      return emails.length === 1 && defaultEmail && emails[0] === defaultEmail;
    },
    // Row X only collapses BCC when it has no chips — remove addresses via chip X.
    canCollapseBcc() {
      return parseEmailList(this.bccEmailsVal).length === 0;
    },
  },
  watch: {
    // immediate so reply-all values from ReplyBox are applied even when
    // they are set before/while this component mounts
    bccEmails: {
      immediate: true,
      handler(newVal) {
        const next = newVal || '';
        if (next !== this.bccEmailsVal) {
          this.bccEmailsVal = next;
        }
        if (next) {
          this.showBcc = true;
        }
      },
    },
    ccEmails: {
      immediate: true,
      handler(newVal) {
        const next = newVal || '';
        if (next !== this.ccEmailsVal) {
          this.ccEmailsVal = next;
        }
      },
    },
    toEmails: {
      immediate: true,
      handler(newVal) {
        const next = newVal || '';
        if (next !== this.toEmailsVal) {
          this.toEmailsVal = next;
        }
        if (next) {
          this.showTo = true;
        }
      },
    },
  },
  validations: {
    ccEmailsVal: {
      hasValidEmails(value) {
        return validEmailsByComma(value);
      },
    },
    bccEmailsVal: {
      hasValidEmails(value) {
        return validEmailsByComma(value);
      },
    },
    toEmailsVal: {
      hasValidEmails(value) {
        return validEmailsByComma(value);
      },
    },
  },
  methods: {
    handleAddTo() {
      this.showTo = true;
      // Prefill with contact so the default recipient stays visible while editing
      if (!this.toEmailsVal && this.defaultToEmail) {
        this.toEmailsVal = this.defaultToEmail;
      }
    },
    handleRemoveTo() {
      if (!this.canCollapseTo) return;
      this.showTo = false;
      this.toEmailsVal = '';
      this.$emit('update:toEmails', '');
    },
    handleAddBcc() {
      this.showBcc = true;
    },
    handleRemoveBcc() {
      if (!this.canCollapseBcc) return;
      this.showBcc = false;
      this.bccEmailsVal = '';
      this.$emit('update:bccEmails', '');
    },
    onToEmailsChange() {
      this.syncToEmails();
      // If every chip was removed, fall back to the default contact To
      if (this.allowToField && this.showTo && !this.toEmailsVal.trim()) {
        this.showTo = false;
      }
    },
    onBccEmailsChange() {
      this.syncBccEmails();
      // If every chip was removed, hide the BCC row
      if (this.showBcc && !this.bccEmailsVal.trim()) {
        this.showBcc = false;
      }
    },
    // Sync one field at a time so a blur/change on CC cannot wipe To/Bcc
    // (and vice versa) when reply-all values are being applied.
    syncToEmails() {
      this.$v.toEmailsVal.$touch();
      this.$emit('update:toEmails', this.toEmailsVal);
    },
    syncCcEmails() {
      this.$v.ccEmailsVal.$touch();
      this.$emit('update:ccEmails', this.ccEmailsVal);
    },
    syncBccEmails() {
      this.$v.bccEmailsVal.$touch();
      this.$emit('update:bccEmails', this.bccEmailsVal);
    },
    syncEmails() {
      this.syncToEmails();
      this.syncCcEmails();
      this.syncBccEmails();
    },
    // Commit typed (not yet chipped) emails before send. Returns false if any
    // field has an invalid draft so send can be blocked.
    flushPendingEmails() {
      const inputs = [
        this.$refs.toChipInput,
        this.$refs.ccChipInput,
        this.$refs.bccChipInput,
      ].filter(Boolean);

      const allValid = inputs.every(input => input.flush());
      this.syncEmails();
      return allValid;
    },
  },
};
</script>
<style lang="scss" scoped>
.input-group-wrap .message {
  @apply text-sm text-red-500 dark:text-red-500;
}
.input-group {
  @apply border-b border-solid border-slate-75 dark:border-slate-700 my-1 items-start;

  .input-group-label {
    @apply border-transparent bg-transparent text-xs font-semibold pl-0 pt-2;
  }
  .input-group-field {
    @apply overflow-hidden;
  }
}

.default-to-chip {
  @apply inline-flex items-center max-w-full truncate rounded-full px-2 py-0.5 my-1
    text-xs font-medium bg-slate-75 text-slate-800
    dark:bg-slate-700 dark:text-slate-100;
}

.input-group.error {
  @apply border-b-red-500 dark:border-b-red-500;
  .input-group-label {
    @apply text-red-500 dark:text-red-500;
  }
}
</style>
